import { FocusEvent, Ref, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '../Ant';
import { InfoLinear } from '../icons';
import { IFeature, StartModule } from '../../interface/featureSelection';
import { useActiveOption } from '../searchableSelect/useActiveOption';
import { PanelRootProps } from '../searchableSelect/searchableSelectPanel';
import {
  StartModuleNote,
  StartModuleOption,
  StartModuleOptions,
  StartModulePanel as Panel,
} from '../../assets/styles/components/featureBox';

interface IStartModulePanelProps {
  ref?: Ref<HTMLDivElement>;
  rootProps: PanelRootProps;
  listboxId: string;
  options: IFeature[];
  value: StartModule;
  onSelect: (startModule: StartModule) => void;
  ariaLabel: string;
}

const StartModulePanel = ({
  ref,
  rootProps,
  listboxId,
  options,
  value,
  onSelect,
  ariaLabel,
}: IStartModulePanelProps) => {
  const { t } = useTranslation();
  const noteId = useId();
  const [listElement, setListElement] = useState<HTMLElement | null>(null);
  const selectOptions = options.map((option) => ({
    value: option.id as StartModule,
    label: option.text,
  }));
  const getOptionId = (index: number) => `${listboxId}-option-${index}`;

  const { activeIndex, activeOptionId, setActiveIndex, handleKeyDown } = useActiveOption({
    options: selectOptions,
    preferredValue: value,
    getOptionId,
    onPick: (option) => onSelect(option.value),
    handlesEdgeKeys: true,
  });

  // The popup focuses its root on open; the list holds the keyboard from there.
  const handleRootFocus = (event: FocusEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) listElement?.focus();
  };

  return (
    <Panel
      ref={ref}
      {...rootProps}
      vertical
      gap={10}
      className="w-full p-10"
      onFocus={handleRootFocus}
      data-testid="start-module-panel"
    >
      <StartModuleOptions
        ref={setListElement}
        vertical
        gap={5}
        id={listboxId}
        role="listbox"
        aria-label={ariaLabel}
        aria-describedby={noteId}
        aria-activedescendant={activeOptionId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {options.map((option, index) => {
          const isSelected = option.id === value;
          return (
            <StartModuleOption
              key={option.id}
              align="center"
              gap={10}
              className="flex-shrink-0 px-10 py-5 fz-13 fw-500 lh-18 cursor-pointer"
              id={getOptionId(index)}
              role="option"
              aria-selected={isSelected}
              data-active={index === activeIndex}
              data-testid={`start-module-option-${option.id}`}
              onMouseMove={() => setActiveIndex(index)}
              onClick={() => onSelect(option.id as StartModule)}
            >
              {isSelected ? option.selectedIcon : option.icon}
              <span className="min-w-0">{option.text}</span>
            </StartModuleOption>
          );
        })}
      </StartModuleOptions>
      <StartModuleNote id={noteId} component="p" align="start" gap={5} className="pt-10">
        <InfoLinear className="fz-12 flex-shrink-0 text-clr-general-description" />
        <Text $fontsize="11px" $lineheight="16px" $color="description">
          {t('common:features.onlyEnabledFeaturesListed')}
        </Text>
      </StartModuleNote>
    </Panel>
  );
};

export default StartModulePanel;
